import React from 'react';
import type { TourEvent } from './tour-event-bus';
import type { TourFieldKey } from './tour-field-state';

/* TimesheetTour step definitions
   ------------------------------
   `buildTimesheetTourSteps(hasAnyClient)` returns the resolved step
   list. Each step's `body` is a FUNCTION `(t) => ReactNode` rather
   than a pre-resolved JSX element so the translation lookups happen
   at RENDER time, not at build time. That means when the user changes
   language mid-tour, the bubble re-renders and immediately shows the
   new language — no rebuild effect needed.

   All tooltip text is keyed under `TOUR_TS_*` in en-us.js and sv-se.js. */
export interface TourStep {
  key: string;
  selector: string;
  /** Function so the translation calls inside it run during every
   *  render of the bubble, not once at step-build time. */
  body: (t: T) => React.ReactNode;
  /** Phase B: auto-advance when this event fires on the tour-event-bus. */
  gateOn?: TourEvent;
  /** Phase C: keep the Next button DISABLED until a predicate over the
   *  field-state snapshot returns true. The hook evaluates each step's
   *  predicate using the current snapshot pushed by the sidebar. */
  predicate?: (snap: Partial<Record<TourFieldKey, unknown>>) => boolean;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

/* Minimal `t` shape — accepting any callable that returns a string so
   we don't have to import i18next's TFunction type here. */
type T = (key: string) => string;

const Body: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="tts-tooltip">
    <div className="tts-tooltip__title">{title}</div>
    <div className="tts-tooltip__body">{children}</div>
  </div>
);

const buildStart = (): TourStep => ({
  key: 'start',
  /* `selector: 'body'` + `position: 'center'` makes reactour render the
     first step as a centered welcome modal instead of a callout anchored
     to the Start Tour button — same pattern used by the completion step. */
  selector: 'body',
  position: 'center',
  body: (t) => <Body title={t('TOUR_TS_START_TITLE')}>{t('TOUR_TS_START_BODY')}</Body>,
});

/* Step 2A — point at the Add Client button. Clicking it opens the
   AddCustomerModal; the modal's mount fires `add-customer-modal-opened`,
   advancing the tour to the modal step below. */
const buildNoClient = (): TourStep => ({
  key: 'no-client',
  selector: '[data-tour="add-client-btn"]',
  body: (t) => <Body title={t('TOUR_TS_NO_CLIENT_TITLE')}>{t('TOUR_TS_NO_CLIENT_BODY')}</Body>,
  gateOn: 'add-customer-modal-opened',
});

/* Step 2A.5 — ONLY inserted in the no-client branch. The Add Customer
   modal is wide (`size="xl"`) so reactour can't fit the bubble to any
   side without overlapping. Instead of letting reactour auto-position
   (which falls back to centering on top of the form), we keep the
   selector on the modal so the mask cutout still highlights it, then
   override the helper's positioning via CSS to PIN the bubble to the
   bottom-right corner of the viewport — see `.tts-helper--corner` in
   timesheet-tour.scss. Gated on `client-created` so saving advances. */
const buildAddCustomerModalStep = (): TourStep => ({
  key: 'add-customer-modal',
  selector: '.add-customer-modal',
  position: 'right',
  body: (t) => <Body title={t('TOUR_TS_ADD_CUSTOMER_MODAL_TITLE')}>{t('TOUR_TS_ADD_CUSTOMER_MODAL_BODY')}</Body>,
  gateOn: 'client-created',
});

const buildHasClient = (): TourStep => ({
  key: 'open-record',
  selector: '[data-tour="timesheet-record-btn"]',
  body: (t) => <Body title={t('TOUR_TS_HAS_CLIENT_TITLE')}>{t('TOUR_TS_HAS_CLIENT_BODY')}</Body>,
  gateOn: 'sidebar-opened',
});

const buildAfterBranchSteps = (): TourStep[] => [
  /* All sidebar-internal steps use position="left" so the bubble sits
     to the LEFT of the sidebar (which slides in from the right). Without
     this, reactour auto-positions and the bubble can land on top of the
     highlighted field, blocking the user from clicking it. */
  {
    key: 'client-select',
    selector: '[data-tour="client-dropdown"]',
    position: 'left',
    body: (t) => <Body title={t('TOUR_TS_CLIENT_SELECT_TITLE')}>{t('TOUR_TS_CLIENT_SELECT_BODY')}</Body>,
    predicate: (snap) => {
      const v = snap['tour:clientId'];
      return typeof v === 'string' && v.trim().length > 0;
    },
  },
  {
    key: 'task-details',
    selector: '[data-tour="task-details"]',
    position: 'left',
    body: (t) => <Body title={t('TOUR_TS_TASK_TITLE')}>{t('TOUR_TS_TASK_BODY')}</Body>,
    predicate: (snap) => {
      const v = snap['tour:taskDetails'];
      return typeof v === 'string' && v.trim().length > 0;
    },
  },
  {
    key: 'from-date',
    selector: '[data-tour="from-date"]',
    position: 'left',
    body: (t) => <Body title={t('TOUR_TS_FROM_DATE_TITLE')}>{t('TOUR_TS_FROM_DATE_BODY')}</Body>,
    predicate: (snap) => {
      const v = snap['tour:fromDate'];
      return typeof v === 'string' && v.length > 0;
    },
  },
  {
    key: 'to-date',
    selector: '[data-tour="to-date"]',
    position: 'left',
    body: (t) => <Body title={t('TOUR_TS_TO_DATE_TITLE')}>{t('TOUR_TS_TO_DATE_BODY')}</Body>,
    predicate: (snap) => {
      const to = snap['tour:toDate'];
      const from = snap['tour:fromDate'];
      if (typeof to !== 'string' || to.length === 0) return false;
      if (typeof from !== 'string' || from.length === 0) return true;
      return to >= from;
    },
  },
  {
    key: 'hours',
    selector: '[data-tour="hours"]',
    position: 'left',
    body: (t) => <Body title={t('TOUR_TS_HOURS_TITLE')}>{t('TOUR_TS_HOURS_BODY')}</Body>,
  },
  {
    key: 'pricing-type',
    selector: '[data-tour="pricing-type"]',
    position: 'left',
    body: (t) => <Body title={t('TOUR_TS_PRICING_TITLE')}>{t('TOUR_TS_PRICING_BODY')}</Body>,
    /* Pricing type has a default value ('Hourly') so it's never empty —
       view-only step. The user can change it if they want. */
  },
  {
    key: 'price',
    selector: '[data-tour="price"]',
    position: 'left',
    body: (t) => <Body title={t('TOUR_TS_PRICE_TITLE')}>{t('TOUR_TS_PRICE_BODY')}</Body>,
    /* Next stays disabled until a positive number is entered. Mirrors
       the sidebar's own validateForm() rule for `price`. */
    predicate: (snap) => {
      const v = snap['tour:price'];
      if (typeof v !== 'string' && typeof v !== 'number') return false;
      const n = parseFloat(String(v));
      return !isNaN(n) && n > 0;
    },
  },
  {
    key: 'submit',
    selector: '[data-tour="submit"]',
    position: 'left',
    body: (t) => <Body title={t('TOUR_TS_SUBMIT_TITLE')}>{t('TOUR_TS_SUBMIT_BODY')}</Body>,
    gateOn: 'submit-success',
  },
  /* Order: Add Entry Week → Week Row.
     Rationale: after Submit, the user is shown HOW to add more entries
     (Step 11 — "Add Entry to Week" button), and then the tour points at
     the newly-added row (Step 12 — "Week Row"). */
  {
    key: 'add-entry-week',
    selector: '[data-tour="add-entry-week"]',
    body: (t) => <Body title={t('TOUR_TS_ADD_ENTRY_TITLE')}>{t('TOUR_TS_ADD_ENTRY_BODY')}</Body>,
    gateOn: 'add-entry-clicked',
  },
  {
    key: 'week-row',
    selector: '[data-tour="week-row"]',
    body: (t) => <Body title={t('TOUR_TS_WEEK_ROW_TITLE')}>{t('TOUR_TS_WEEK_ROW_BODY')}</Body>,
  },
  {
    key: 'save-timesheet',
    selector: '[data-tour="save-timesheet"]',
    body: (t) => <Body title={t('TOUR_TS_SAVE_TITLE')}>{t('TOUR_TS_SAVE_BODY')}</Body>,
    gateOn: 'save-clicked',
  },
  {
    key: 'confirm-yes',
    selector: '[data-tour="confirm-yes"]',
    body: (t) => <Body title={t('TOUR_TS_CONFIRM_TITLE')}>{t('TOUR_TS_CONFIRM_BODY')}</Body>,
    gateOn: 'confirm-yes-clicked',
  },
  {
    key: 'completion',
    selector: 'body',
    position: 'center',
    body: (t) => (
      <Body title={t('TOUR_TS_COMPLETION_TITLE')}>
        {t('TOUR_TS_COMPLETION_LEAD')}
        <ul style={{ margin: '8px 0 0 0', paddingLeft: '18px' }}>
          <li>{t('TOUR_TS_COMPLETION_ITEM_1')}</li>
          <li>{t('TOUR_TS_COMPLETION_ITEM_2')}</li>
          <li>{t('TOUR_TS_COMPLETION_ITEM_3')}</li>
          <li>{t('TOUR_TS_COMPLETION_ITEM_4')}</li>
        </ul>
        <div style={{ marginTop: '10px' }}>{t('TOUR_TS_COMPLETION_OUTRO')}</div>
        {/* Reminder: the tour is never permanently dismissed — Start Tour
            stays in the page header so the user can replay anytime. */}
        <div style={{ marginTop: '12px', fontSize: '12.5px', color: '#7c3aed', fontWeight: 600 }}>
          {t('TOUR_TS_COMPLETION_RESTART_HINT')}
        </div>
      </Body>
    ),
  },
];

/* Resolves the final step list based on whether the user already has
   any clients in the system. No translation function needed any more —
   each step's body is itself a function that calls t() at render time
   (see TourBubble in TimesheetTour.tsx). */
export const buildTimesheetTourSteps = (hasAnyClient: boolean): TourStep[] => {
  const branchSteps = hasAnyClient
    ? [buildHasClient()]
    : [buildNoClient(), buildAddCustomerModalStep(), buildHasClient()];
  return [buildStart(), ...branchSteps, ...buildAfterBranchSteps()];
};
